import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../api/client';

export default function DJsForm({ editMode = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = editMode && Boolean(id);

  const [formData, setFormData] = useState({
    stage_name: '',
    user_id: '',
    bio: '',
    photo_url: '',
    is_active: true,
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchUsers();
    if (isEdit) fetchDJ();
  }, [id]);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get('/api/users', { params: { limit: 200 } });
      setUsers(Array.isArray(data) ? data : data.users || []);
    } catch (err) {
      console.error('[DJsForm] Failed to fetch users:', err);
    }
  };

  const fetchDJ = async () => {
    try {
      const { data } = await api.get(`/api/djs/${id}`);
      setFormData({
        stage_name: data.stage_name || '',
        user_id: data.user_id || '',
        bio: data.bio || '',
        photo_url: data.photo_url || '',
        is_active: data.is_active ?? true,
      });
    } catch (err) {
      console.error('[DJsForm] Fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.stage_name.trim()) {
      setErrors({ stage_name: 'El nombre es obligatorio' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, user_id: formData.user_id || null };
      if (isEdit) {
        await api.put(`/api/djs/${id}`, payload);
      } else {
        await api.post('/api/djs', payload);
      }
      navigate('/djs');
    } catch (err) {
      console.error('[DJsForm] Save failed:', err);
      setErrors({ submit: 'Error al guardar el DJ.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-[300px] items-center justify-center"><p className="text-dark-400">Cargando DJ...</p></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">{isEdit ? 'Editar DJ' : 'Nuevo DJ'}</h1>
        <Link to="/djs" className="btn btn-secondary btn-sm">Volver a DJs</Link>
      </div>

      {errors.submit && (
        <div className="rounded-lg bg-error-900/30 border border-error-700 p-3 text-sm text-error-400">{errors.submit}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 max-w-xl">
        <div>
          <label className="block text-sm font-medium text-dark-300 mb-1">Nombre / Apodo *</label>
          <input
            type="text"
            name="stage_name"
            value={formData.stage_name}
            onChange={handleChange}
            className={`input ${errors.stage_name ? 'border-error-500' : ''}`}
            placeholder="Ej: DJ Mariano"
          />
          {errors.stage_name && <p className="text-xs text-error-400 mt-1">{errors.stage_name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-dark-300 mb-1">Bio / descripción</label>
          <textarea
            name="bio"
            value={formData.bio}
            onChange={handleChange}
            className="input min-h-[80px]"
            placeholder="Descripción del DJ (opcional)"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-dark-300 mb-1">Vincular cuenta de acceso (opcional)</label>
          <select name="user_id" value={formData.user_id} onChange={handleChange} className="input">
            <option value="">Sin cuenta (solo admin por ahora)</option>
            {users.filter((u) => u.role === 'dj' || !formData.user_id).map((u) => (
              <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
            ))}
          </select>
          <p className="text-xs text-dark-500 mt-1">Se vincula después para darle acceso al DJ. No es necesario ahora.</p>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="is_active"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
          />
          <label htmlFor="is_active" className="text-sm text-dark-300">Activo</label>
        </div>

        <div className="flex justify-end gap-3 border-t border-dark-700 pt-6">
          <Link to="/djs" className="btn btn-secondary">Cancelar</Link>
          <button type="submit" disabled={saving} className="btn btn-primary">
            {saving ? 'Guardando...' : isEdit ? 'Actualizar DJ' : 'Crear DJ'}
          </button>
        </div>
      </form>
    </div>
  );
}
